import { create } from 'zustand';
import apiClient from '../api/client';

interface NoteMetadata {
  id: string;
  title: string;
  created: string;
  modified: string;
  tags: string[];
}

interface NoteDetail extends NoteMetadata {
  content: string;
}

interface NotesState {
  notes: NoteMetadata[];
  activeNote: NoteDetail | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  fetchNotes: () => Promise<void>;
  openNote: (id: string) => Promise<NoteDetail | undefined>;
  createNote: (title: string) => Promise<NoteDetail | undefined>;
  updateNote: (id: string, content: string, title?: string, tags?: string[]) => Promise<void>;
  deleteNote: (id: string) => Promise<void>;
  clearActiveNote: () => void;
}

export const useNotesStore = create<NotesState>((set) => ({
  notes: [],
  activeNote: null,
  loading: false,
  saving: false,
  error: null,

  fetchNotes: async () => {
    set({ loading: true, error: null });
    try {
      const data = await apiClient.get<{ notes: NoteMetadata[] }>('/api/drive/notes');
      set({ notes: data.notes, loading: false });
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  openNote: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const note = await apiClient.get<NoteDetail>(`/api/drive/notes/${id}`);
      set({ activeNote: note, loading: false });
      return note;
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
      return undefined;
    }
  },

  createNote: async (title: string) => {
    set({ saving: true, error: null });
    try {
      const note = await apiClient.post<NoteDetail>('/api/drive/notes', {
        title,
        content: '',
        tags: [],
      });
      set((state) => ({
        notes: [{ id: note.id, title: note.title, created: note.created, modified: note.modified, tags: note.tags }, ...state.notes],
        activeNote: note,
        saving: false,
      }));
      return note;
    } catch (err) {
      set({ error: (err as Error).message, saving: false });
      return undefined;
    }
  },

  updateNote: async (id, content, title, tags) => {
    set({ saving: true });
    try {
      const body: Record<string, unknown> = { content };
      if (title !== undefined) body.title = title;
      if (tags !== undefined) body.tags = tags;
      const updated = await apiClient.put<NoteDetail>(`/api/drive/notes/${id}`, body);
      set((state) => ({
        activeNote: updated,
        saving: false,
        notes: state.notes.map((n) =>
          n.id === id
            ? { id: updated.id, title: updated.title, created: updated.created, modified: updated.modified, tags: updated.tags }
            : n
        ),
      }));
    } catch (err) {
      set({ error: (err as Error).message, saving: false });
    }
  },

  deleteNote: async (id: string) => {
    try {
      await apiClient.delete(`/api/drive/notes/${id}`);
      set((state) => ({
        notes: state.notes.filter((n) => n.id !== id),
        activeNote: state.activeNote?.id === id ? null : state.activeNote,
      }));
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  clearActiveNote: () => set({ activeNote: null }),
}));
